import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  ls,
  ss,
  telemetryErrorKey,
  tokenKey,
  unauthorizedCode,
  unauthorizedToken,
} from "consts";
import { BaseReponseType, TelemetryErrorType } from "models";
import { clearLocalStorage, clearObject, objectToURLParams } from "utils";

const maxTelemetryError = 50;

const telemetryErrors = JSON.parse(ls.getItem(telemetryErrorKey) ?? "[]");

const addTelemetryError = (data: TelemetryErrorType) => {
  const list = [data, ...telemetryErrors.slice(0, maxTelemetryError - 1)];
  ls.setItem(telemetryErrorKey, JSON.stringify(list));
};

const CancelToken = axios.CancelToken;
const pendingRequest = new Map();

const basePath = process.env.REACT_APP_BASE_URL;
const CRMPath = "CRMCloudWebAPI";
const DSProxyPath = "DSWebProxyAPI";

const service = axios.create({
  baseURL: `${basePath}/${CRMPath}`,
  timeout: 120000,
  paramsSerializer: { indexes: null },
});

const serviceDS = axios.create({
  baseURL: `${basePath}/${DSProxyPath}`,
  timeout: 120000,
  paramsSerializer: { indexes: null },
});

const onReqResolved = (request: InternalAxiosRequestConfig<any>) => {
  const currentToken = ss.getItem(tokenKey);

  if (currentToken)
    request.params = { pDeToken: currentToken, ...request.params };

  request.params = clearObject(request.params);

  const requestKey = getReqKey(request);

  if (pendingRequest.has(requestKey)) removeReqKey(requestKey);
  else
    request.cancelToken = new CancelToken((cancel) =>
      pendingRequest.set(requestKey, cancel),
    );

  return request;
};

const onReqRejected = (error: any) => Promise.reject(error);

service.interceptors.request.use(onReqResolved, onReqRejected);
serviceDS.interceptors.request.use(onReqResolved, onReqRejected);

const logOutUser = () => {
  const currentToken = ss.getItem(tokenKey);
  if (currentToken) {
    clearLocalStorage();
    window.location.replace(
      `/authentication?${objectToURLParams({ loggedOut: true })}`,
    );
  }
};

const onResResolved = (response: AxiosResponse<any, any>) => {
  removeReqKey(getReqKey(response.config));

  if (response.data?.idReturnAPI < 0)
    addTelemetryError({
      status: response.status,
      message: response.data?.deReturnAPI,
      path: response.request?.responseURL ?? "",
      response: JSON.stringify(response.data),
    });

  if (response.data?.idReturnAPI === unauthorizedCode) logOutUser();

  return response;
};

const onResRejected = (error: AxiosError) => {
  if (error.config) removeReqKey(getReqKey(error.config));

  if (axios.isCancel(error)) return new Promise(() => {});

  const path =
    error.request?.responseURL ||
    `${error.config?.baseURL}/${error.config?.url}`;

  const response =
    JSON.stringify(error.response?.data) ||
    `${error.code} - ${error.config?.data}`;

  addTelemetryError({
    status: error.status ?? "",
    message: error.message,
    path,
    response,
  });

  if (error?.response?.status === unauthorizedToken) logOutUser();

  return Promise.reject(error);
};

service.interceptors.response.use(onResResolved, onResRejected);
serviceDS.interceptors.response.use(onResResolved, onResRejected);

const getReqKey = (config: AxiosRequestConfig) =>
  `${config?.method}-${config?.url}}`;

const removeReqKey = (key: string) => {
  const cancelToken = pendingRequest.get(key);
  cancelToken?.(key);
  pendingRequest.delete(key);
};

export const fetchSync = <T extends BaseReponseType>(
  method: string,
  path: string,
  params?: object,
  body?: XMLHttpRequestBodyInit,
): T => {
  const pDeToken = ss.getItem(tokenKey);

  let baseURL = process.env.REACT_APP_BASE_URL;

  const URL = `${baseURL}/${path}?${Object.entries({ pDeToken, ...params })
    .map(([key, value]) => `${key}=${value}`)
    .join("&")}`;

  const res = new XMLHttpRequest();
  res.open(method, URL, false);
  res.send(body);

  if (res.status === unauthorizedToken) logOutUser();
  else if (res.status !== 200) {
    addTelemetryError({
      status: res.status ?? "",
      message: res.statusText,
      path: res.responseURL,
      response: res.responseText,
    });

    throw new Error(res.statusText, { cause: res });
  }

  const response: T = JSON.parse(res.response);

  if (response.idReturnAPI < 0)
    addTelemetryError({
      status: res.status ?? "",
      message: response.deReturnAPI,
      path: res.responseURL,
      response: JSON.stringify(response),
    });

  if (response.idReturnAPI === unauthorizedCode) logOutUser();

  return response;
};

export { serviceDS };
export default service;
